"use client";

import { memo, useMemo } from "react";
import { FacepileCore } from "./core";
import {
	FestivalInteractionCard,
	SetShowInteractionCard,
} from "./interaction-cards";
import {
	festivalAttendanceSort,
	hasEngagementFilter,
	hasFestivalStatusFilter,
	setShowEngagementSort,
} from "./strategies";
import type {
	ArtistFacepileProps,
	FestivalFacepileProps,
	FestivalInteraction,
	GenericFacepileProps,
	SetShowInteraction,
	ShowFacepileProps,
} from "./types";

/**
 * Generic Facepile - Used for sets across different contexts
 * Maintains 100% backward compatibility with existing usage
 */
export const Facepile = memo((props: GenericFacepileProps) => {
	const {
		interactions,
		set,
		festivalSlug,
		maxAvatars = 5,
		size = "sm",
		className,
		allowHoverInModal = false,
		disableLinks = false,
	} = props;

	// Context object for generating links
	const context = useMemo(
		() => ({
			getHref: (item: SetShowInteraction) => {
				if (disableLinks) return undefined;
				if (festivalSlug && set?.id) {
					return `/festivals/${festivalSlug}/${set.id}`;
				}
				return "#";
			},
			set,
			festivalSlug,
		}),
		[disableLinks, festivalSlug, set],
	);

	// Get status badge renderer from props or use default
	const statusBadgeRenderer = props.statusBadgeRenderer;

	return (
		<FacepileCore
			items={interactions}
			maxVisible={maxAvatars}
			size={size}
			className={className}
			sortStrategy={setShowEngagementSort}
			// No filter - generic facepile shows all users
			renderInteraction={(item) => (
				<SetShowInteractionCard
					interaction={item}
					statusBadgeRenderer={statusBadgeRenderer}
				/>
			)}
			context={context}
			enableHoverCards={true}
			enableLinks={!disableLinks}
			modalAware={allowHoverInModal}
			platform="web"
		/>
	);
});

Facepile.displayName = "Facepile";

/**
 * Festival Facepile - Used for festival-specific contexts
 * Maintains 100% backward compatibility with existing usage
 */
export const FestivalFacepile = memo((props: FestivalFacepileProps) => {
	const {
		attendees,
		maxAvatars = 5,
		size = "sm",
		className,
		festivalEndDate,
	} = props;

	// Check if festival is past
	const isPast = useMemo(() => {
		if (!festivalEndDate) return false;

		const now = new Date();
		now.setHours(0, 0, 0, 0);
		const endDate = new Date(festivalEndDate);
		endDate.setHours(23, 59, 59, 999);
		return endDate < now;
	}, [festivalEndDate]);

	// Get festival status badge renderer from props or use default
	const statusBadgeRenderer = props.statusBadgeRenderer;

	return (
		<FacepileCore
			items={attendees}
			maxVisible={maxAvatars}
			size={size}
			className={className}
			sortStrategy={festivalAttendanceSort}
			filterStrategy={hasFestivalStatusFilter}
			renderInteraction={(item) => (
				<FestivalInteractionCard
					interaction={item}
					isPast={isPast}
					statusBadgeRenderer={statusBadgeRenderer}
				/>
			)}
			enableHoverCards={true}
			enableLinks={false} // Festival facepile doesn't have links
			platform="web"
		/>
	);
});

FestivalFacepile.displayName = "FestivalFacepile";

/**
 * Show Facepile - Used for show-specific contexts
 * Maintains 100% backward compatibility with existing usage
 */
export const ShowFacepile = memo((props: ShowFacepileProps) => {
	const { interactions, set, maxAvatars = 5, size = "sm", className } = props;

	// Get status badge renderer from props or use default
	const statusBadgeRenderer = props.statusBadgeRenderer;

	return (
		<FacepileCore
			items={interactions}
			maxVisible={maxAvatars}
			size={size}
			className={className}
			sortStrategy={setShowEngagementSort}
			filterStrategy={hasEngagementFilter}
			renderInteraction={(item) => (
				<SetShowInteractionCard
					interaction={item}
					statusBadgeRenderer={statusBadgeRenderer}
				/>
			)}
			context={{ set }}
			enableHoverCards={true}
			enableLinks={false} // Show facepile doesn't have links
			platform="web"
		/>
	);
});

ShowFacepile.displayName = "ShowFacepile";

/**
 * Artist Facepile - Identical to ShowFacepile
 * Maintains 100% backward compatibility with existing usage
 */
export const ArtistsFacepile = ShowFacepile;
